/**
 * Global axios configuration.
 * Import this file once at the app entry point to ensure
 * all axios requests include credentials (HttpOnly cookies).
 *
 * Also sets up:
 *  - 429 response interceptor → waits and retries once when rate-limited
 *  - 401 response interceptor → clears auth state and redirects to login
 *  - Background token refresh every 30 min to keep the session alive
 */
import axios from 'axios';
import { AUTH_ENDPOINTS } from '../constants/api';

// Send cookies with every cross-origin request so the
// HttpOnly auth cookie is transmitted automatically.
axios.defaults.withCredentials = true;

/* ──────────────────── 429 interceptor ──────────────────── */
// When the server returns 429 (rate-limited), wait and retry once
// instead of surfacing a confusing error or triggering a logout.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 429 &&
      !originalRequest._retryAfterRateLimit
    ) {
      // Use the Retry-After header if present, otherwise default to 5 s
      const retryAfterSec = parseInt(error.response.headers['retry-after'], 10) || 5;
      const delayMs = Math.min(retryAfterSec * 1000, 30000); // cap at 30 s

      originalRequest._retryAfterRateLimit = true;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return axios(originalRequest);
    }

    return Promise.reject(error);
  }
);

/* ──────────────────── 401 interceptor ──────────────────── */
let isRefreshing = false;
let refreshPromise = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip auth-related endpoints
    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/admin-login') ||
      originalRequest?.url?.includes('/auth/refresh') ||
      originalRequest._retry // already retried
    ) {
      return Promise.reject(error);
    }

    // Attempt a single token refresh before logging out
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = axios.post(AUTH_ENDPOINTS.REFRESH)
        .then(() => { isRefreshing = false; })
        .catch(() => {
          isRefreshing = false;
          localStorage.removeItem('bhaAuthToken');
          window.location.href = '/login';
          return Promise.reject(error);
        });
    }

    // Wait for the refresh to finish, then retry the original request
    try {
      await refreshPromise;
      originalRequest._retry = true;
      return axios(originalRequest);
    } catch (_) {
      return Promise.reject(error);
    }
  }
);

/* ──────────────── background token refresh ─────────────── */
const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // every 30 min

function startTokenRefresh() {
  setInterval(async () => {
    // Only refresh if the user appears to be logged in
    if (!localStorage.getItem('bhaAuthToken')) return;
    try {
      await axios.post(AUTH_ENDPOINTS.REFRESH);
    } catch (_) {
      // If refresh fails (e.g. token already expired), the 401 interceptor handles it
    }
  }, REFRESH_INTERVAL_MS);
}

startTokenRefresh();

export default axios;
